import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { LIBRARY_CATEGORIES, SEARCH_CARD } from "@/lib/libraryCategories";
import { LibraryBreadcrumb, breadcrumbJsonLd } from "@/components/LibraryBreadcrumb";

/**
 * The Living Library — homepage.
 * The central knowledge foundation of the Academy of Practical Wisdom.
 * Phase 1: the architecture. Knowledge before decoration — no scroll
 * theatrics, just a calm, premium reading surface that will grow.
 */

function CategoryCard({ id, Icon, to, index }) {
  const { t } = useT();
  // The Virtues card carries its own approved artwork — the Compass Rose
  // IS the front cover. No black tile (Founder directive, 2026-07).
  const artwork = id === "virtues" ? "/virtues-compass-desktop.jpg" : null;
  return (
    <Link
      to={to}
      data-testid={`library-card-${id}`}
      className="group relative flex flex-col h-full overflow-hidden border border-white/10 hover:border-gold/50
        bg-white/[0.015] hover:bg-gold/[0.03] p-7 md:p-8
        transition-[transform,box-shadow,border-color,background-color] duration-300
        hover:-translate-y-1 hover:shadow-[0_18px_50px_-24px_rgba(232,200,121,0.35)]"
    >
      {artwork && (
        <>
          <img
            src={artwork}
            alt=""
            aria-hidden
            data-testid={`library-card-${id}-artwork`}
            className="absolute inset-0 w-full h-full object-cover object-center
              transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/20"
          />
        </>
      )}
      <div className="relative flex items-start justify-between gap-4">
        <Icon size={21} strokeWidth={1.4} className="text-gold/80 group-hover:text-gold transition-colors" />
        <span className={`text-[9px] tracking-[0.28em] uppercase transition-colors ${artwork ? "text-bone/60 group-hover:text-gold/80" : "text-bone/30 group-hover:text-gold/60"}`}>
          {index}
        </span>
      </div>
      <h3 className={`relative mt-7 font-serif text-xl md:text-2xl text-bone leading-tight ${artwork ? "[text-shadow:0_1px_14px_rgba(0,0,0,0.8)]" : ""}`}>
        {t(`library.cats.${id}.title`)}
      </h3>
      <p className={`relative mt-3 text-sm font-reading leading-relaxed flex-1 ${artwork ? "text-bone/85 [text-shadow:0_1px_10px_rgba(0,0,0,0.85)]" : "text-fog"}`}>
        {t(`library.cats.${id}.desc`)}
      </p>
      <div className={`relative mt-7 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase transition-colors group-hover:text-gold ${artwork ? "text-bone/70" : "text-bone/40"}`}>
        {t("library.card.open")}
        <ArrowRight size={11} strokeWidth={1.75} />
      </div>
    </Link>
  );
}

export default function LivingLibrary() {
  const { t, lang } = useT();

  useSEO({
    title: "The Living Library | StoicOs.ai",
    description: t("library.seo.description"),
    canonical: langCanonical("/library", lang),
    alternates: langAlternates("/library"),
    og: {
      title: "The Living Library | StoicOs.ai",
      description: t("library.seo.description"),
      image: "https://stoicos.ai/og-library.jpg",
    },
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "The Living Library",
        url: "https://stoicos.ai/library",
        description: t("library.seo.description"),
        isPartOf: { "@type": "WebSite", name: "StoicOs.ai", url: "https://stoicos.ai" },
        hasPart: LIBRARY_CATEGORIES.map((c) => ({
          "@type": "CollectionPage",
          name: t(`library.cats.${c.id}.title`),
          url: `https://stoicos.ai/library/${c.id}`,
        })),
      },
      breadcrumbJsonLd([
        { label: t("academy.nav"), href: "https://stoicos.ai/academy" },
        { label: t("library.hero.title"), href: "https://stoicos.ai/library" },
      ]),
    ],
  });

  const scrollToCollections = (e) => {
    e.preventDefault();
    document.getElementById("library-collections")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="library-page" className="bg-ink text-bone page-fade-in">

      {/* =============================================================
          HERO — standing before the Living Library.
          Bright and cinematic: the golden sunlight and marble
          reflections stay untouched; only a soft gradient sits behind
          the text for readability. Desktop 100vh · tablet 80vh ·
          mobile ~68vh. Very slow 40s Ken Burns.
          ============================================================= */}
      <section
        data-testid="library-hero"
        className="relative w-full h-[68svh] min-h-[480px] md:h-[80svh] lg:h-[100svh] overflow-hidden bg-ink"
      >
        <div className="library-hero-kenburns absolute inset-0">
          <picture className="block w-full h-full">
            <source media="(min-width: 1024px)" srcSet="/library-hero.jpg" />
            <img
              src="/library-hero-mobile.jpg"
              alt={t("library.hero.imageAlt")}
              data-testid="library-hero-image"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover object-center"
            />
          </picture>
        </div>

        {/* Soft top scrim — keeps the fixed navigation readable over the bright sky */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-28
          bg-gradient-to-b from-black/45 to-transparent" />
        {/* Subtle gradient behind the text only — the architecture stays bright */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-[52%]
          bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

        {/* Text overlay — HTML only, never baked into the artwork */}
        <div className="absolute inset-x-0 bottom-0 px-6 sm:px-10 pb-10 md:pb-16">
          <div className="max-w-3xl mx-auto text-center">
            <h1
              data-testid="library-hero-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.08] tracking-tight
                [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]"
            >
              {t("library.hero.title")}
            </h1>
            <p className="mt-4 font-serif italic text-lg md:text-2xl text-gold leading-relaxed
              [text-shadow:0_1px_16px_rgba(0,0,0,0.6)]">
              {t("library.hero.subtitle")}
            </p>
            <p className="mt-5 text-sm md:text-base text-bone/90 font-reading leading-[1.8]
              [text-shadow:0_1px_12px_rgba(0,0,0,0.7)]">
              {t("library.hero.body1")}
              <br />
              {t("library.hero.body2")}
              <br />
              {t("library.hero.body3")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8">
              <a
                href="#library-collections"
                onClick={scrollToCollections}
                data-testid="library-hero-cta"
                className="inline-flex items-center justify-center gap-3 px-9 py-4 bg-gold hover:bg-gold-hover
                  text-ink text-[11px] tracking-[0.32em] uppercase font-semibold transition-colors
                  shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7)]"
              >
                {t("library.hero.cta")}
                <ArrowRight size={14} strokeWidth={2} />
              </a>
              <Link
                to="/academy"
                data-testid="library-hero-back"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase
                  text-bone/85 hover:text-gold transition-colors
                  [text-shadow:0_1px_12px_rgba(0,0,0,0.7)]"
              >
                <ArrowLeft size={13} strokeWidth={1.75} />
                {t("library.hero.backAcademy")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Collections */}
      <section
        id="library-collections"
        data-testid="library-collections"
        className="relative py-24 md:py-32 px-6 sm:px-10 scroll-mt-24"
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <LibraryBreadcrumb
              items={[
                { label: t("academy.nav"), to: "/academy" },
                { label: t("library.hero.title") },
              ]}
              testid="library-home-breadcrumb"
            />
          </div>
          <div className="flex items-center gap-4 mb-10">
            <h2 className="text-[11px] tracking-[0.4em] uppercase text-gold whitespace-nowrap">
              {t("library.collections.title")}
            </h2>
            <span className="h-px flex-1 bg-white/10" aria-hidden />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {LIBRARY_CATEGORIES.map((c, i) => (
              <CategoryCard
                key={c.id}
                id={c.id}
                Icon={c.Icon}
                to={`/library/${c.id}`}
                index={String(i + 1).padStart(2, "0")}
              />
            ))}
            <CategoryCard
              id={SEARCH_CARD.id}
              Icon={SEARCH_CARD.Icon}
              to="/library/search"
              index={String(LIBRARY_CATEGORIES.length + 1).padStart(2, "0")}
            />
          </div>

          <p className="mt-14 text-center text-sm text-fog italic font-serif" data-testid="library-growing-note">
            {t("library.growing")}
          </p>
        </div>
      </section>
    </main>
  );
}
